export function sameMonthAndYear(date1: string, date2:string) {
  const d1 = new Date(date1)
  const d2 = new Date(date2)
  const m1 = d1.getMonth()
  const m2 = d2.getMonth()
  const y1 = d1.getFullYear()
  const y2 = d2.getFullYear()
  return m1 === m2 && y1 === y2
}

type TimeUnit = 'seconds' | 'minutes' | 'hours' | 'days' | 'weeks'
const millisecondsMappingTable: Record<TimeUnit, number> = {
  seconds: 1000,
  minutes: 1000 * 60,
  hours: 1000 * 60 * 60,
  days: 1000 * 60 * 60 * 24,
  weeks: 1000 * 60 * 60 * 24 * 7
}

export function subtractFromDate(date: Date, amount: number, unit: TimeUnit): Date {
  const millisToSubtract = amount * millisecondsMappingTable[unit]
  return new Date(date.valueOf() - millisToSubtract)
}

export function addToDate(date: Date, amount: number, unit: TimeUnit): Date {
  const millisToAdd = amount * millisecondsMappingTable[unit]
  return new Date(date.valueOf() + millisToAdd)
}

export function timeDiff(date1: string, date2: string, unit: TimeUnit): number {
  const millisecondsDifference = Math.abs(new Date(date2).valueOf() - new Date(date1).valueOf())
  return millisecondsDifference / millisecondsMappingTable[unit]
}

export function withinDateRange(startDate: string, endDate: string, currentDate: string): boolean {
  return new Date(currentDate) >= new Date(startDate) && new Date(currentDate) <= new Date(endDate)
}

// Calendar date (YYYY-MM-DD) of the given date in the user's timezone. Unlike
// toISOString(), this doesn't shift to the previous day for timezones ahead of UTC.
export function toLocalDateString(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

// Parses the calendar date part of a date string as local midnight. new Date('YYYY-MM-DD')
// parses as UTC midnight, which is the previous day for timezones behind UTC.
export function parseLocalDate(dateString: string): Date {
  const [ year, month, day ] = dateString.substring(0, 10).split('-').map(Number)
  return new Date(year, month - 1, day)
}

export function monthsAgoDateRange(months: number): [string, string] {
  const end = new Date()
  const start = new Date(end)
  start.setMonth(start.getMonth() - months)
  return [
    start.toISOString().substring(0, 10),
    end.toISOString().substring(0, 10)
  ]
}

export function monthsBetween(from: Date, to: Date): number {
  const years = to.getFullYear() - from.getFullYear()
  const months = to.getMonth() - from.getMonth()
  return Math.max(0, years * 12 + months)
}

export function daysBetween(from: Date, to: Date): number {
  const ms = to.getTime() - from.getTime()
  return Math.max(0, Math.floor(ms / millisecondsMappingTable.days))
}

export function safeParseDate(iso: string | null | undefined): Date | null {
  if (!iso) return null
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? null : d
}
